import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { APP_URL } from '../../config';
import {
  getDifficultyHexColor,
  getDifficultyLabel,
} from '../../utils/difficultyUtils';
import {
  AccessTime as AccessTimeIcon,
  MenuBook as MenuBookIcon,
  VideoLibrary as VideoLibraryIcon,
  Slideshow as SlideshowIcon,
  Quiz as QuizIcon,
  Star as StarIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Bookmark as BookmarkIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  EditNote as DraftIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Button from '../UIKit/Button';
import VideoModal from './VideoModal';
import RatingsModal from './RatingsModal';
import StudentModal from './StudentModal';
import AuthorsModal from './AuthorsModal';

const HeroContainer = styled(Box)(({ theme, backgroundImage }) => ({
  position: 'relative',
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  borderRadius: theme.spacing(1.5),
  color: 'white',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[4],
  minHeight: '400px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)`,
    color: 'white',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
    minHeight: '500px',
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '640px', // 40rem equivalent
  zIndex: 2,
  position: 'relative',
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(0.5, 1),
  fontSize: '0.875rem',
}));

const StatItem = styled(Box)(({ theme, isClickable = false }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  transition: 'background-color 0.2s ease',
  cursor: isClickable ? 'pointer' : 'default',
  '&:hover': isClickable ? {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  } : {},
}));

const DifficultyChip = styled(Chip)(({ theme, difficulty }) => ({
  backgroundColor: getDifficultyHexColor(difficulty),
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const DraftChip = styled(Chip)(() => ({
  backgroundColor: '#F59E0B',
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
  '& .MuiChip-icon': {
    color: 'white',
  },
}));

const ProfessionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const ProfessionChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  fontSize: '0.75rem',
  height: '24px',
  fontWeight: 500,
  borderRadius: '6px',
  '& .MuiChip-label': {
    padding: '0 8px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

const ToolChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  fontSize: '0.75rem',
  height: '24px',
  fontWeight: 500,
  borderRadius: '6px',
  cursor: 'pointer',
  '& .MuiChip-label': {
    padding: '0 8px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

const HeroSection = ({
  course,
  courseStats,
  isEnrolled,
  isBookmarked,
  onToggleBookmark,
  onContinueLearning,
  onEnroll,
  formatDuration,
  onScrollToReviews,
  userRating,
  onRatingSubmit,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useTranslation(['courses', 'common']);
  const { user } = useAuth();

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showRatingsModal, setShowRatingsModal] = useState(false);
  const [showProfessionsModal, setShowProfessionsModal] = useState(false);
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showAuthorsModal, setShowAuthorsModal] = useState(false);

  const isAuthenticated = !!user;

  const handleOpenVideoModal = useCallback(() => {
    if (courseStats?.videoContent && courseStats.videoContent.length > 0) {
      setShowVideoModal(true);
    }
  }, [courseStats]);

  const handleCloseVideoModal = useCallback(() => {
    setShowVideoModal(false);
  }, []);

  const handleOpenRatingsModal = useCallback(() => {
    setShowRatingsModal(true);
  }, []);

  const handleCloseRatingsModal = useCallback(() => {
    setShowRatingsModal(false);
  }, []);

  const handleOpenProfessionsModal = useCallback(() => {
    setShowProfessionsModal(true);
  }, []);

  const handleCloseProfessionsModal = useCallback(() => {
    setShowProfessionsModal(false);
  }, []);

  const handleOpenToolsModal = useCallback(() => {
    setShowToolsModal(true);
  }, []);

  const handleCloseToolsModal = useCallback(() => {
    setShowToolsModal(false);
  }, []);

  const handleOpenStudentsModal = useCallback(() => {
    setShowStudentsModal(true);
  }, []);

  const handleCloseStudentsModal = useCallback(() => {
    setShowStudentsModal(false);
  }, []);

  const handleOpenAuthorsModal = useCallback(() => {
    setShowAuthorsModal(true);
  }, []);

  const handleCloseAuthorsModal = useCallback(() => {
    setShowAuthorsModal(false);
  }, []);

  const getCourseImage = () => {
    if (course?.image) {
      // Если это data URL или полный URL, возвращаем как есть
      if (course.image.startsWith('http') || course.image.startsWith('data:')) {
        return course.image;
      }
      // Иначе добавляем APP_URL
      return `${APP_URL}${course.image}`;
    }
    return `${APP_URL}/images/courses/default-${
      course?.category?.slug || 'default'
    }.jpg`;
  };

  const handleContinueClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isEnrolled) {
      onContinueLearning?.();
    } else {
      onEnroll?.();
    }
  };

  const getButtonText = () => {
    if (!isAuthenticated) {
      return t('courses:courseDetail.loginToEnroll');
    }
    if (isEnrolled) {
      return t('courses:courseDetail.continueStudying');
    }
    return t('courses:courseDetail.enrollCourse');
  };

  const handleScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer backgroundImage={getCourseImage()} className="course-header">
      <ContentWrapper>
        {/* Badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {course?.isDraft && (
            <DraftChip
              icon={<DraftIcon sx={{ fontSize: 16 }} />}
              label="DRAFT"
            />
          )}
          <DifficultyChip
            label={`${getDifficultyLabel(course?.difficulty)} LEVEL`}
            difficulty={course?.difficulty}
            className="course-category"
          />
        </Box>

        {/* Title */}
        <Typography
          variant={isMobile ? 'h4' : 'h3'}
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 2,
            lineHeight: 1.2,
          }}
        >
          {course?.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            lineHeight: 1.6,
            mb: 3,
          }}
          className="course-description"
        >
          {course?.description}
        </Typography>

        {/* Professions */}
        {course?.professions && course.professions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('courses:courseDetail.forWhom')}:
              </Typography>
              <ProfessionsContainer>
                {course.professions.slice(0, 4).map((profession, index) => {
                  // Обробляємо різні структури даних професій
                  const professionName =
                    profession.term_group?.MainTerm?.value ||
                    profession.term_group?.name ||
                    profession.name ||
                    profession.title ||
                    'Unknown Profession';

                  return (
                    <ProfessionChip
                      key={profession.id || profession._id || index}
                      label={professionName}
                      size="small"
                    />
                  );
                })}
                {course.professions.length > 4 && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={handleOpenProfessionsModal}
                  >
                    More...
                  </Typography>
                )}
              </ProfessionsContainer>
            </Box>
          </Box>
        )}

        {/* Tools */}
        {course?.tools && course.tools.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('courses:courseDetail.tools')}:
              </Typography>
              <ProfessionsContainer>
                {course.tools.slice(0, 2).map((tool, index) => {
                  const toolName = tool.name || 'Unknown Tool';
                  const hasLink = !!tool.link;
                  
                  if (hasLink) {
                    return (
                      <Link
                        key={tool.id || tool._id || index}
                        href={tool.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{ display: 'inline-block' }}
                      >
                        <ToolChip
                          label={toolName}
                          size="small"
                        />
                      </Link>
                    );
                  }
                  
                  return (
                    <ToolChip
                      key={tool.id || tool._id || index}
                      label={toolName}
                      size="small"
                      sx={{ cursor: 'default' }}
                    />
                  );
                })}
                {course.tools.length > 2 && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={handleOpenToolsModal}
                  >
                    More...
                  </Typography>
                )}
              </ProfessionsContainer>
            </Box>
          </Box>
        )}

        {/* Continue Learning Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={handleContinueClick}
            fullWidth={isMobile}
            sx={{
              px: isMobile ? 2 : 4,
              py: 1.5,
            }}
            className={
              isEnrolled ? 'course-continue-button' : 'course-enroll-button'
            }
          >
            {getButtonText()}
          </Button>
        </Box>
      </ContentWrapper>

      {/* Course Statistics */}
      <StatsContainer>
        {/* Duration */}
        <StatItem>
          <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
          {formatDuration(courseStats?.duration, t)}
        </StatItem>

        {/* Lessons */}
        <StatItem
          isClickable={true}
          onClick={() => handleScrollToSection('lessons-section')}
        >
          <MenuBookIcon sx={{ fontSize: 16, mr: 1 }} />
          {courseStats?.lessonCount || 0}{' '}
          {t('courses:courseDetail.heroStats.lessons')}
        </StatItem>

        {/* Videos */}
        {courseStats?.videoContent?.length > 0 && (
          <StatItem isClickable={true} onClick={handleOpenVideoModal}>
            <VideoLibraryIcon sx={{ fontSize: 16, mr: 1 }} />
            {courseStats.videoContent.length}{' '}
            {t('courses:courseDetail.heroStats.videos')}
          </StatItem>
        )}

        {/* Presentations */}
        {courseStats?.presentationCount > 0 && (
          <StatItem>
            <SlideshowIcon sx={{ fontSize: 16, mr: 1 }} />
            {courseStats.presentationCount}{' '}
            {t('courses:courseDetail.heroStats.presentations')}
          </StatItem>
        )}

        {/* Tests */}
        <StatItem
          onClick={() =>
            document
              .getElementById('lessons-section')
              ?.scrollIntoView({ behavior: 'smooth' })
          }
        >
          <QuizIcon sx={{ fontSize: 16, mr: 1 }} />
          {courseStats?.testCount || 0}{' '}
          {t('courses:courseDetail.heroStats.tests')}
        </StatItem>

        {/* Rating - Clickable */}
        <StatItem isClickable={true} onClick={handleOpenRatingsModal}>
          <StarIcon sx={{ fontSize: 16, mr: 1, color: '#FFD700' }} />
          {course?.rating && course.rating > 0
            ? course.rating.toFixed(1)
            : t('courses:courseDetail.noRating')}
        </StatItem>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Reviews - Clickable */}
        <StatItem isClickable={true} onClick={onScrollToReviews}>
          <MessageIcon sx={{ fontSize: 16, mr: 1 }} />
          {t('courses:courseDetail.heroStats.reviews')}
        </StatItem>

        {/* Students Count */}
        <StatItem 
          isClickable={isAuthenticated} 
          onClick={isAuthenticated ? handleOpenStudentsModal : undefined}
        >
          <PeopleIcon sx={{ fontSize: 16, mr: 1 }} />
          {courseStats?.totalStudents || 0}{' '}
          {t('courses:courseDetail.heroStats.students')}
        </StatItem>

        {/* Authors Modal Button */}
        <StatItem 
          isClickable={isAuthenticated} 
          onClick={isAuthenticated ? handleOpenAuthorsModal : undefined}
        >
          <PersonIcon sx={{ fontSize: 16, mr: 1 }} />
          {t('courses:courseDetail.heroStats.author')}
        </StatItem>

        {/* Bookmark Button */}
        {isAuthenticated && (
          <IconButton
            onClick={onToggleBookmark}
            sx={{
              color: 'white',
              padding: 1,
              borderRadius: theme.spacing(1),
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: 20 }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        )}
      </StatsContainer>

      {/* Модальное окно для видео */}
      <VideoModal
        open={showVideoModal}
        onClose={handleCloseVideoModal}
        videos={courseStats?.videoContent || []}
      />

      {/* Модальное окно для рейтингов */}
      <RatingsModal
        open={showRatingsModal}
        onClose={handleCloseRatingsModal}
        courseId={course?._id}
        courseRating={course?.rating || 0}
        ratingCount={course?.ratingCount || 0}
        userRating={userRating || 0}
        onSubmitRating={onRatingSubmit}
        isEnrolled={isEnrolled}
      />

      {/* Модальное окно для профессий */}
      <Dialog
        open={showProfessionsModal}
        onClose={handleCloseProfessionsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: '42rem', // max-w-2xl equivalent
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            {t('courses:courseDetail.allProfessions', 'This course is suitable for')}
          </Typography>
          <IconButton
            onClick={handleCloseProfessionsModal}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2,
              maxHeight: '60vh',
              overflowY: 'auto',
              p: 0.5,
            }}
          >
            {course?.professions?.map((profession, index) => {
              const professionName =
                profession.term_group?.MainTerm?.value ||
                profession.term_group?.name ||
                profession.name ||
                profession.title ||
                'Unknown Profession';

              return (
                <Box
                  key={profession.id || profession._id || index}
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(55, 65, 81, 0.8)' 
                      : 'rgba(243, 244, 246, 0.8)',
                    padding: 1,
                    borderRadius: 1,
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    color: theme.palette.text.primary,
                  }}
                >
                  {professionName}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {isAuthenticated && (
        <StudentModal
          open={showStudentsModal}
          onClose={handleCloseStudentsModal}
          courseId={course?._id}
        />
      )}
      {isAuthenticated && (
        <AuthorsModal
          open={showAuthorsModal}
          onClose={handleCloseAuthorsModal}
          courseId={course?._id}
        />
      )}

      {/* Tools Modal */}
      <Dialog
        open={showToolsModal}
        onClose={handleCloseToolsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: '42rem', // max-w-2xl equivalent
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            {t('courses:courseDetail.tools')}
          </Typography>
          <IconButton
            onClick={handleCloseToolsModal}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2,
              maxHeight: '60vh',
              overflowY: 'auto',
              p: 0.5,
            }}
          >
            {course?.tools?.map((tool, index) => {
              const toolName = tool.name || 'Unknown Tool';
              const hasLink = !!tool.link;
              
              const toolBoxStyles = {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(55, 65, 81, 0.8)' 
                  : 'rgba(243, 244, 246, 0.8)',
                padding: 1,
                borderRadius: 1,
                textAlign: 'center',
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
              };

              if (hasLink) {
                return (
                  <Link
                    key={tool.id || tool._id || index}
                    href={tool.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{ 
                      display: 'block',
                      ...toolBoxStyles,
                      ':hover': {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    {toolName}
                  </Link>
                );
              }
              
              return (
                <Box
                  key={tool.id || tool._id || index}
                  sx={toolBoxStyles}
                >
                  {toolName}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </HeroContainer>
  );
};

export default HeroSection;
